/**
 * Leave policy groups panel (V370 leave entitlement engine), rendered under
 * the leave types list on Settings → Leave types.
 *
 * A group gives its members a different policy for chosen leave types (e.g.
 * Managers: 21 annual days; everyone else: the leave type's 14). Types
 * without an override keep the default. Saving applies the change to the
 * members' balances immediately on the backend — additive true-ups only, so
 * manual adjustments and approved leave are never overwritten.
 *
 * Business logic (drafts, validation, membership diff) lives in
 * `leave_policy_groups_panel_utils.ts`.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  Checkbox,
  GlassCard,
  Input,
  SkeletonList,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import type { LeaveType } from '../../data/use_leave_types';
import {
  useLeavePolicyGroups,
  type LeaveGroupAccrualMethod,
  type LeavePolicyGroup,
} from '../../data/use_leave_policy_groups';
import { useEmployees } from '../../data/use_employees';
import { worker_type_capabilities } from '../../lib/worker_types';
import {
  draft_to_group_input,
  group_to_draft,
  make_empty_group_draft,
  membership_changes,
  other_group_by_employee,
  validate_group_draft,
  type GroupDraft,
  type GroupDraftErrors,
  type GroupRuleDraft,
} from './leave_policy_groups_panel_utils';
import {
  format_days_input,
  parse_days_scaled,
} from './leave_types_panel_utils';

const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

interface RuleRowProps {
  rule: GroupRuleDraft;
  label: string;
  method_options: StyledSelectOption[];
  on_change: (patch: Partial<GroupRuleDraft>) => void;
}

function RuleRow({
  rule,
  label,
  method_options,
  on_change,
}: RuleRowProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <div className="glass-soft rounded-xl p-3 flex flex-col gap-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={rule.enabled}
          onCheckedChange={(next) => on_change({ enabled: next })}
        />
        <span className="text-[13.5px] font-medium">
          {t('settings.leave_groups.override_label', { leave_type: label })}
        </span>
      </label>
      {rule.enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="nv-label">
              {t('settings.leave_types.field_accrual')}
            </span>
            <StyledSelect
              value={rule.accrual_method}
              options={method_options}
              ariaLabel={t('settings.leave_types.field_accrual')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
              onChange={(v) =>
                on_change({ accrual_method: v as LeaveGroupAccrualMethod })
              }
            />
          </label>
          {rule.accrual_method !== 'none' && (
            <label className="flex flex-col gap-1">
              <span className="nv-label">
                {t('settings.leave_types.field_entitlement')}
              </span>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={format_days_input(rule.annual_entitlement_days_scaled)}
                onChange={(e) =>
                  on_change({
                    annual_entitlement_days_scaled: parse_days_scaled(
                      e.target.value,
                    ),
                  })
                }
              />
            </label>
          )}
          {rule.accrual_method !== 'none' && (
            <label className="flex flex-col gap-1">
              <span className="nv-label">
                {t('settings.leave_types.field_carry_forward_max')}
              </span>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={
                  rule.carry_forward_capped
                    ? format_days_input(rule.carry_forward_max_days_scaled)
                    : ''
                }
                placeholder={t('settings.leave_types.carry_forward_unlimited')}
                onChange={(e) => {
                  const scaled = parse_days_scaled(e.target.value);
                  on_change({
                    carry_forward_capped: scaled !== null,
                    carry_forward_max_days_scaled: scaled,
                  });
                }}
              />
            </label>
          )}
          {rule.accrual_method !== 'none' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={rule.prorate_on_join}
                onCheckedChange={(next) => on_change({ prorate_on_join: next })}
              />
              <span className="text-[13.5px]">
                {t('settings.leave_types.field_prorate_on_join')}
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

export interface LeavePolicyGroupsPanelProps {
  leave_types: readonly LeaveType[];
}

export function LeavePolicyGroupsPanel({
  leave_types,
}: LeavePolicyGroupsPanelProps): React.JSX.Element | null {
  const { t } = useTranslation('wp');
  const { state, groups, upsert, assign } = useLeavePolicyGroups();
  const { employees: all_employees } = useEmployees();
  // A contractor accrues no leave and the API refuses to assign them a
  // policy group, so they are never offered as members.
  const employees = all_employees.filter(
    (emp) => worker_type_capabilities(emp.employment_type).leave,
  );

  // `null` = closed; `''` = creating; otherwise the code being edited.
  const [editing_code, set_editing_code] = useState<string | null>(null);
  const [draft, set_draft] = useState<GroupDraft | null>(null);
  const [errors, set_errors] = useState<GroupDraftErrors>({});
  const [save_failure, set_save_failure] = useState<string | null>(null);
  const [is_saving, set_is_saving] = useState(false);

  const method_options: StyledSelectOption[] = [
    {
      value: 'fixed_annual',
      label: t('settings.leave_types.accrual_fixed_annual'),
    },
    { value: 'monthly', label: t('settings.leave_types.accrual_monthly') },
    { value: 'none', label: t('settings.leave_groups.accrual_none') },
  ];

  function label_for(code: string): string {
    return leave_types.find((lt) => lt.code === code)?.name ?? code;
  }

  function open_editor(group: LeavePolicyGroup | null): void {
    set_editing_code(group?.code ?? '');
    set_draft(
      group !== null
        ? group_to_draft(group, leave_types)
        : make_empty_group_draft(leave_types),
    );
    set_errors({});
    set_save_failure(null);
  }

  function close_editor(): void {
    set_editing_code(null);
    set_draft(null);
    set_errors({});
    set_save_failure(null);
  }

  async function handle_save(): Promise<void> {
    if (draft === null || upsert === undefined || assign === undefined) return;
    const found = validate_group_draft(draft, t);
    if (Object.keys(found).length > 0) {
      set_errors(found);
      return;
    }
    set_is_saving(true);
    set_save_failure(null);
    const saved = await upsert(draft_to_group_input(draft));
    if (saved.outcome === 'failure') {
      set_is_saving(false);
      set_save_failure(
        saved.failure === 'forbidden'
          ? t('settings.leave_groups.error_forbidden')
          : saved.failure === 'invalid'
            ? t('settings.leave_groups.error_invalid')
            : t('settings.leave_groups.error_save_generic'),
      );
      return;
    }
    const current =
      groups.find((g) => g.code === draft.code)?.employee_ids ?? [];
    const changes = membership_changes(draft.code, current, draft.member_ids);
    const results = await Promise.all(
      changes.map((c) => assign(c.employee_id, c.group_code)),
    );
    set_is_saving(false);
    if (results.some((r) => r.outcome === 'failure')) {
      set_save_failure(t('settings.leave_groups.error_members'));
      return;
    }
    close_editor();
  }

  if (state.status === 'no_entity' || state.status === 'entitlement_required') {
    // The leave types panel above already explains both states.
    return null;
  }

  const other_groups =
    draft !== null
      ? other_group_by_employee(groups, draft.code)
      : new Map<string, string>();

  return (
    <div className="flex flex-col gap-3 mt-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-[14.5px] font-bold">
            {t('settings.leave_groups.title')}
          </h2>
          <p className="text-[13px] text-[color:var(--ink-muted)] mt-0.5">
            {t('settings.leave_groups.description')}
          </p>
        </div>
        {upsert !== undefined && editing_code === null && (
          <Button
            variant="secondary"
            size="sm"
            data-testid="leave-group-add"
            onClick={() => open_editor(null)}
          >
            {t('settings.leave_groups.add_button')}
          </Button>
        )}
      </div>

      {state.status === 'loading' && (
        <GlassCard className="rounded-2xl p-5">
          <SkeletonList rows={2} />
        </GlassCard>
      )}

      {state.status === 'error' && (
        <Alert variant="danger" title={t('settings.leave_groups.error_title')}>
          {t('settings.leave_groups.error_description')}
        </Alert>
      )}

      {draft !== null && (
        <GlassCard className="rounded-2xl p-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="nv-label">
                {t('settings.leave_groups.field_name')}
              </span>
              <Input
                type="text"
                value={draft.name}
                onChange={(e) => set_draft({ ...draft, name: e.target.value })}
              />
              {errors.name !== undefined && (
                <p className="nv-help is-error">{errors.name}</p>
              )}
            </label>
            <label className="flex flex-col gap-1">
              <span className="nv-label">
                {t('settings.leave_types.field_code')}
              </span>
              <Input
                type="text"
                value={draft.code}
                disabled={editing_code !== ''}
                onChange={(e) => set_draft({ ...draft, code: e.target.value })}
              />
              {errors.code !== undefined && (
                <p className="nv-help is-error">{errors.code}</p>
              )}
            </label>
          </div>

          {draft.rules.length === 0 ? (
            <p className="nv-help">{t('settings.leave_groups.no_types')}</p>
          ) : (
            draft.rules.map((rule, index) => (
              <RuleRow
                key={rule.leave_type}
                rule={rule}
                label={label_for(rule.leave_type)}
                method_options={method_options}
                on_change={(patch) =>
                  set_draft({
                    ...draft,
                    rules: draft.rules.map((r, i) =>
                      i === index ? { ...r, ...patch } : r,
                    ),
                  })
                }
              />
            ))
          )}
          {errors.rules !== undefined && (
            <p className="nv-help is-error">{errors.rules}</p>
          )}

          <div className="flex flex-col gap-1">
            <span className="nv-label">
              {t('settings.leave_groups.field_members')}
            </span>
            {employees.length === 0 ? (
              <p className="nv-help">
                {t('settings.leave_groups.no_employees')}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {employees.map((emp) => {
                  const checked = draft.member_ids.includes(emp.id);
                  const other = other_groups.get(emp.id);
                  return (
                    <label
                      key={emp.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(next) =>
                          set_draft({
                            ...draft,
                            member_ids: next
                              ? [...draft.member_ids, emp.id]
                              : draft.member_ids.filter((id) => id !== emp.id),
                          })
                        }
                      />
                      <span className="text-[13.5px]">{emp.name}</span>
                      {other !== undefined && !checked && (
                        <span className="text-[12px] text-[color:var(--ink-muted)]">
                          {t('settings.leave_groups.member_of_other', {
                            group: other,
                          })}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {save_failure !== null && (
            <Alert
              variant="danger"
              title={t('settings.leave_groups.save_error_title')}
            >
              {save_failure}
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={close_editor}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={is_saving}
              data-testid="leave-group-save"
              onClick={() => {
                void handle_save();
              }}
            >
              {t('settings.leave_types.save')}
            </Button>
          </div>
        </GlassCard>
      )}

      {state.status === 'ready' && (
        <GlassCard className="rounded-2xl p-0 overflow-hidden">
          {groups.length === 0 && (
            <div className="p-5">
              <p className="text-[13px] text-[color:var(--ink-muted)]">
                {t('settings.leave_groups.empty')}
              </p>
            </div>
          )}
          {groups.map((g) => (
            <div
              key={g.code}
              className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--line)] last:border-0"
            >
              <div>
                <span className="text-[13.5px] font-medium">{g.name}</span>
                <p className="text-[12px] text-[color:var(--ink-muted)] mt-0.5">
                  {t('settings.leave_groups.summary', {
                    members: g.employee_ids.length,
                    rules: g.rules.length,
                  })}
                </p>
              </div>
              {upsert !== undefined && editing_code === null && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => open_editor(g)}
                >
                  {t('common.edit')}
                </Button>
              )}
            </div>
          ))}
        </GlassCard>
      )}
    </div>
  );
}
